import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Organization } from '../../types';
import { API } from '../api_clients';

interface OrganizationStore {
    // State
    organization: Organization | null;
    organizations: Organization[];
    subscription: API.ResponsesSubscriptionResponse | null;

    // Actions
    setOrganization: (organization: Organization | null) => void;
    setOrganizations: (organizations: Organization[]) => void;
    updateOrganization: (updates: Partial<Organization>) => void;
    setSubscription: (subscription: API.ResponsesSubscriptionResponse | null) => void;
    updateSubscription: (updates: Partial<API.ResponsesSubscriptionResponse>) => void;
    clearOrganization: () => void;
    clearOrganizations: () => void;
    clearSubscription: () => void;
}

const initialState = {
    organization: null,
    organizations: [],
    subscription: null,
};

export const useOrganizationStore = create<OrganizationStore>()(
    persist(
        (set) => ({
            ...initialState,

            setOrganization: (organization) => set({
                organization,
                // Don't update subscription from organization object
                // Subscription is managed separately via setSubscription
            }),

            setOrganizations: (organizations) => {
                const filteredOrganizations = organizations.filter(
                    org => !org.roles.includes('driver')
                )
                set({ organizations: filteredOrganizations })
            },

            updateOrganization: (updates) => set((state) => {
                if (!state.organization) return state;

                return {
                    organization: { ...state.organization, ...updates },
                    // Don't sync subscription from organization
                    // Subscription is managed separately
                };
            }),

            setSubscription: (subscription) => set({
                subscription,
                // Don't sync back to organization object (different types)
            }),

            updateSubscription: (updates) => set((state) => {
                if (!state.subscription) return state;

                return {
                    subscription: { ...state.subscription, ...updates },
                    // Don't sync back to organization object (different types)
                };
            }),

            clearOrganization: () => set({ organization: null, subscription: null }),

            clearOrganizations: () => set({ organizations: [] }),

            clearSubscription: () => set({
                subscription: null,
                // Don't sync back to organization object (different types)
            }),
        }),
        {
            name: 'organization-storage',
            partialize: (state) => ({
                organization: state.organization,
                organizations: state.organizations,
                subscription: state.subscription,
            }),
        }
    )
);
